import { ArrowLeft, Loader2, MailCheck, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

interface VerifyEmailNoticeProps {
	email: string;
	renewAfter: string;
	resending: boolean;
	onResend: () => void;
	onChangeEmail: () => void;
	devLink?: string | null;
}

function secondsUntil(targetIso: string): number {
	return Math.max(
		0,
		Math.ceil((new Date(targetIso).getTime() - Date.now()) / 1000),
	);
}

function useCountdown(targetIso: string): number {
	const [remaining, setRemaining] = useState(() => secondsUntil(targetIso));

	useEffect(() => {
		setRemaining(secondsUntil(targetIso));
		const id = window.setInterval(() => {
			setRemaining((prev) => {
				if (prev <= 1) {
					window.clearInterval(id);
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
		return () => window.clearInterval(id);
	}, [targetIso]);

	return remaining;
}

export function VerifyEmailNotice({
	email,
	renewAfter,
	resending,
	onResend,
	onChangeEmail,
	devLink,
}: VerifyEmailNoticeProps) {
	const cooldown = useCountdown(renewAfter);
	const canResend = cooldown === 0 && !resending;

	return (
		<Card>
			<CardContent className="verify-email-notice">
				<span className="verify-email-notice__icon">
					<MailCheck />
				</span>

				<div className="verify-email-notice__heading">
					<h1 className="verify-email-notice__title">Confirme seu e-mail</h1>
					<p className="verify-email-notice__description">
						Enviamos um link de confirmação para{" "}
						<span className="verify-email-notice__email">{email}</span>. Abra a
						mensagem e clique no link para ativar sua conta.
					</p>
				</div>

				<p className="verify-email-notice__hint">
					Não recebeu? Verifique a caixa de spam ou reenvie o e-mail abaixo.
				</p>

				<div className="verify-email-notice__actions">
					<Button
						type="button"
						className="verify-email-notice__resend"
						disabled={!canResend}
						onClick={onResend}
					>
						{resending ? <Loader2 className="auth-spinner" /> : <RefreshCw />}
						{cooldown > 0
							? `Reenviar e-mail em ${cooldown}s`
							: "Reenviar e-mail"}
					</Button>
					<Button
						type="button"
						variant="ghost"
						className="verify-email-notice__change"
						onClick={onChangeEmail}
					>
						<ArrowLeft />
						Usar outro e-mail
					</Button>
				</div>

				{devLink && (
					<div className="verify-email-notice__dev-panel">
						<span className="verify-email-notice__dev-title">
							Ambiente de desenvolvimento
						</span>
						<span>
							Ainda não há back-end de e-mail. Use o link abaixo para simular o
							clique na mensagem.
						</span>
						<a href={devLink} className="verify-email-notice__dev-link">
							Abrir link de verificação
						</a>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
